import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';
import { MenuItem } from '@mui/material';
import styles from '../CustomOfferDetailsPage.module.scss';
import CloseIcon from '@mui/icons-material/Close';
import { SimpleModal } from '../../../../components/Modal';
import { StyledButton } from "components/Buttons/DefaultButton/DefaultButton.styles";
import { StyledSelect, FieldLabel } from '../../../hygienist/AvailableJobPage/components/OfferModal/OfferModal.styles';
import Api from 'api';
import { PartialOffer } from "utils/types";
import { exitReact } from 'utils';
import { Analytics } from 'utils/analytics';


interface DeclineOfferProps {
    partialOffer: PartialOffer;
    offerId: number
  }
  
  interface Button {
    text: string;
    onClick: () => void;
    primary: boolean;
  }
  
  interface ModalContent {
    title: string;
    content: string | React.ReactNode;
    buttons: Button[];
  }
  
  interface RejectData {
    reason: string;
    counterOfferRate?: number;
  }

// @ts-ignore
const TempStars = window.TempStars;
// @ts-ignore
const app = window.app as any;

const DeclineOffer: React.FC<DeclineOfferProps> = ({ partialOffer, offerId }) => {
    const history = useHistory();
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<ModalContent>({title: "", content: "", buttons:[]});
    const [currentOfferId, setCurrentOfferId] = useState<number>(offerId);
    const [counterOfferRate, setCounterOfferRate] = useState<number | ''>('');
    const [counterOfferMode, setCounterOfferMode] = useState<{ offerId: number; rejectData: any } | null>(null);

    const job = partialOffer?.job;

    const menuProps = {
        disablePortal: true,
        disableScrollLock: true,
        marginThreshold: 0,
        PaperProps: {
            className: 'no-fastclick',
            style: {
                maxHeight: '50vh',
                overflowY: 'auto' as const,
                WebkitOverflowScrolling: 'touch' as const,
                touchAction: 'manipulation',
                overscrollBehavior: 'contain',
                paddingBottom: 8,
            },
        },
        MenuListProps: {
            className: 'no-fastclick',
            style: {
                maxHeight: 'inherit',
                overflowY: 'auto' as const,
                WebkitOverflowScrolling: 'touch' as const,
                paddingBottom: 12,
                paddingTop: 4,
            },
        },
    };
    
    useEffect(() => {
        setCurrentOfferId(offerId);
      }, [offerId]);    


      const declineButtonHandler = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setModalContent({
          title: 'Decline this Offer?',
          content: '',
          buttons: [
            { text: 'Decline', onClick: () => popupDeclineSurvey(currentOfferId), primary: true },
            { text: 'Cancel', onClick: () => setIsModalOpen(false), primary: false }
          ]
        });
        setIsModalOpen(true);
      }, [currentOfferId]);

      const popupDeclineSurvey = useCallback((offerId: number) => {
        setModalContent({
          title: 'Help us improve by giving a reason for declining:',
          content: '',
          buttons: [
            { text: 'Hourly rate above our budget', onClick: () => counterOfferJob(offerId, { reason: 'Hourly rate above our budget' }), primary: true },
            { text: 'Looking for more experience', onClick: () => declineJob(offerId, { reason: 'Looking for more experience' }), primary: true  },
            { text: 'None', onClick: () => declineJob(offerId, { reason: 'None' }), primary: true  }
          ]
        });
        setIsModalOpen(true);
      }, []);

    const renderCounterOfferSelector = (rate: number) => {
        const proposedRate = Number(partialOffer?.job?.proposedRate);
        const hasProposedRate = Number.isFinite(proposedRate);
        const counterOfferMinimum = Math.max(1, rate - 10);
        const jobMinimum = hasProposedRate ? Math.max(1, proposedRate) : counterOfferMinimum;
        const minRate = hasProposedRate && rate < proposedRate ? counterOfferMinimum : jobMinimum;
        const maxRate = rate - 1;
        const adjustedMinRate = minRate > maxRate ? Math.max(1, maxRate) : minRate;

        const options: number[] = [];
        for (let i = maxRate; i >= adjustedMinRate; i--) {
            options.push(i);
        }

        return (
            <div style={{ marginTop: 8 }}>
                <FieldLabel>Select Your Acceptable Rate</FieldLabel>
                <StyledSelect
                    value={counterOfferRate}
                    onChange={(e) => setCounterOfferRate(Number(e.target.value))}
                    variant="outlined"
                    fullWidth
                    displayEmpty
                    MenuProps={menuProps}
                    className="no-fastclick"
                    aria-label="counter-offer-rate-select"
                    id="counter-offer-rate-select"
                    data-testid="counter-offer-rate-select"
                    SelectDisplayProps={{ className: 'no-fastclick' }}
                    renderValue={(selected) => {
                        if (!selected) return <span style={{ color: '#999' }}>Select Hourly Rate</span>;
                        return `$${selected}/hr`;
                    }}
                >
                    {options.map((value) => (
                        <MenuItem
                            key={value}
                            value={value}
                            className="no-fastclick"
                            aria-label={`counter-rate-option-${value}`}
                            data-testid={`counter-rate-option-${value}`}
                        >
                            <span style={{ fontWeight: 600 }}>${value}/hr</span>
                        </MenuItem>
                    ))}
                </StyledSelect>
            </div>
        );
    };

    const decliningCustomOfferPopup = useCallback((text: string = '') => {
        setModalContent({
            title: 'Thank you!',
            content: text,
            buttons: [
                {
                    text: 'OK',
                    onClick: () => {
                        setIsModalOpen(false);
                        history.push({ pathname: 'dentistHome' });
                    }, primary: true 
                }
            ]
        });
        setIsModalOpen(true);
    }, [job]);

    const declineJob = useCallback(async (offerId: number, rejectData: RejectData, text: string = "") => {
        try {
          await Api.rejectPartialOffer(offerId, rejectData);
          Analytics.track('decline offer', partialOffer);
          decliningCustomOfferPopup(text);  
        } catch (err) {
          setModalContent({
            title: 'Error',
            content: err instanceof Error ? `Error declining offer. ${err.message}` : 'Error declining offer. Please try again.',
            buttons: [{ text: 'OK', onClick: () => setIsModalOpen(false), primary: true }]
          });
          setIsModalOpen(true);
        }
      }, [decliningCustomOfferPopup, partialOffer]);

    const counterOfferJob = useCallback((offerId: number, rejectData: any) => {
        setCounterOfferRate('');
        setCounterOfferMode({ offerId, rejectData });
        setIsModalOpen(true);
    }, []);

    return (
        <>
          <StyledButton
            variant="outlined"
            color="error"
            startIcon={<CloseIcon />}
            size="small"
            id="continue-button"
            className={styles.declineButton}
            onClick={declineButtonHandler}
          >
            Decline
          </StyledButton>
          <SimpleModal isOpen={isModalOpen} onClose={() => { setIsModalOpen(false); setCounterOfferMode(null); }}>
            <div style={{ padding: '12px 16px' }}>
              {counterOfferMode ? (
                <>
                  <h2 style={{ marginBottom: 20, fontSize: 18, fontWeight: 600 }}>Counter Offer</h2>
                  <div style={{ marginBottom: 24 }}>
                    {renderCounterOfferSelector(partialOffer?.hourlyRate || 0)}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                    <StyledButton
                      onClick={() => {
                        if (counterOfferRate) {
                          declineJob(counterOfferMode.offerId, { ...counterOfferMode.rejectData, counterOfferRate: Number(counterOfferRate) }, 'Your counter offer has been sent.');
                          setCounterOfferMode(null);
                        } else {
                          alert('Please select an acceptable rate.');
                        }
                      }}
                      variant="contained"
                      style={{ width: '100%' }}
                    >
                      Submit Counter Offer
                    </StyledButton>
                    <StyledButton
                      onClick={() => { setIsModalOpen(false); setCounterOfferMode(null); }}
                      variant="outlined"
                      style={{ width: '100%' }}
                    >
                      Cancel
                    </StyledButton>
                  </div>
                </>
              ) : (
                <>
                  <h2 style={{ marginBottom: 20, fontSize: 18, fontWeight: 600 }}>{modalContent?.title}</h2>
                  {modalContent?.content && (
                    <div style={{ marginBottom: 24 }}>{modalContent.content}</div>
                  )}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 12 }}>
                    {modalContent?.buttons && modalContent.buttons.map((button, index) => (
                      <StyledButton key={index} onClick={button.onClick} variant={button.primary ? "contained" : "outlined"} style={{ width: '100%' }}>
                        {button.text}
                      </StyledButton>
                    ))}
                  </div>
                </>
              )}
            </div>
          </SimpleModal>
        </>
    );
};

export default DeclineOffer;